import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  TextInput,
  Button,
  Group,
  Select,
  Switch,
  Stack,
  Checkbox,
  Text,
  Badge,
  Alert,
  SimpleGrid,
  Paper,
} from "@mantine/core"
import { DateInput } from "@mantine/dates"
import { Dropzone } from "@mantine/dropzone"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import type { Document, DocumentFormValues } from "../models/document"
import {
  useCreateDocument,
  useUpdateDocument,
  useDocumentCategories,
  useDocumentTypes,
} from "../hooks/useDocuments"

interface Props {
  doc?: Document | null
  onSuccess: () => void
}

const DocumentFormContent = forwardRef<{ submit: () => void }, Props>(({ doc, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!doc

  const { data: categoriesData } = useDocumentCategories()
  const { data: typesData } = useDocumentTypes()
  const createMutation = useCreateDocument()
  const updateMutation = useUpdateDocument()
  const [file, setFile] = useState<File | null>(null)

  const form = useForm<DocumentFormValues>({
    initialValues: {
      category_id: null,
      description: "",
      valid_until: null,
      public: 0,
      rules: [],
    },
    validate: {
      description: (v) => (v?.length >= 3 ? null : t("documents.validation.descriptionRequired")),
      category_id: (v) => (v ? null : t("documents.validation.categoryRequired")),
    },
  })

  useEffect(() => {
    if (doc) {
      form.setValues({
        category_id: doc.category_id,
        description: doc.description,
        valid_until: doc.valid_until,
        public: doc.public ? 1 : 0,
        rules: doc.rules ?? [],
      })
    }
  }, [doc])

  const handleApiErrors = (data: unknown) => {
    const d = data as Record<string, unknown> | null
    if (!d || d.success !== false) return false
    const errors = (d.errors as Record<string, string[]>) ?? {}
    const formErrors: Record<string, string> = {}
    for (const [key, msgs] of Object.entries(errors)) {
      if (key === "file") {
        setFileError(msgs[0])
      } else {
        formErrors[key] = msgs[0]
      }
    }
    if (Object.keys(formErrors).length > 0) form.setErrors(formErrors)
    return true
  }

  const handleSubmit = (values: DocumentFormValues) => {
    if (isEdit && doc) {
      updateMutation.mutate(
        { id: doc.id, values: { ...values, file: file ?? undefined } },
        {
          onSuccess: (data) => {
            if (handleApiErrors(data)) return
            notifications.show({ color: "green", title: t("common.success"), message: t("documents.updateSuccess") })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({ color: "red", title: t("common.error"), message: err.message })
          },
        },
      )
    } else {
      createMutation.mutate(
        { ...values, file: file ?? undefined },
        {
          onSuccess: (data) => {
            if (handleApiErrors(data)) return
            notifications.show({ color: "green", title: t("common.success"), message: t("documents.createSuccess") })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({ color: "red", title: t("common.error"), message: err.message })
          },
        },
      )
    }
  }

  const categoryOptions = (categoriesData ?? []).map((c: { id: number; name: string }) => ({
    value: String(c.id),
    label: c.name,
  }))

  const roleOptions = (typesData?.roles ?? []).map((r: string) => ({
    value: r,
    label: r,
  }))

  const isPending = createMutation.isPending || updateMutation.isPending

  const [fileError, setFileError] = useState<string | null>(null)

  const handleFormSubmit = (values: DocumentFormValues) => {
    if (!isEdit && !file) {
      setFileError(t("documents.validation.fileRequired"))
      return
    }
    setFileError(null)
    handleSubmit(values)
  }

  const handleSubmitWrapper = form.onSubmit(handleFormSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleSubmitWrapper() }))

  return (
    <form onSubmit={handleSubmitWrapper}>
      {(createMutation.isError || updateMutation.isError) && (
        <Alert color="red" mb="md" withCloseButton onClose={() => { createMutation.reset(); updateMutation.reset() }}>
          {createMutation.error?.message || updateMutation.error?.message}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Paper withBorder p="md" radius="md" style={{ minHeight: 220 }}>
          <Dropzone
            onDrop={(files) => setFile(files[0])}
            accept={["*/*"]}
            maxSize={50 * 1024 * 1024}
            multiple={false}
            styles={{ inner: { pointerEvents: "none" } }}
          >
            <Stack align="center" justify="center" gap="sm" style={{ minHeight: 180 }}>
              <Dropzone.Accept>
                <Text size="xl" c="green">
                  <i className="fa fa-cloud-upload" />
                </Text>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <Text size="xl" c="red">
                  <i className="fa fa-ban" />
                </Text>
              </Dropzone.Reject>
              <Dropzone.Idle>
                <Text size="xl" c="dimmed">
                  <i className="fa fa-file-o" />
                </Text>
              </Dropzone.Idle>
              <Text size="sm" ta="center">
                {isEdit ? t("documents.replaceFile") : t("documents.file")}
              </Text>
              <Text size="xs" c="dimmed" ta="center">
                Arraste o ficheiro aqui ou clique para selecionar<br />
                (max 50MB)
              </Text>
            </Stack>
          </Dropzone>
          {file && (
            <Group mt="sm" gap="xs">
              <Badge variant="light" size="lg">{file.name}</Badge>
              <Button
                variant="subtle"
                color="red"
                size="compact-xs"
                onClick={() => setFile(null)}
              >
                <i className="fa fa-times" />
              </Button>
            </Group>
          )}
          {isEdit && !file && doc?.real_name && (
            <Text size="xs" c="dimmed" mt="xs" ta="center">
              {t("documents.currentFile")}: {doc.real_name}
            </Text>
          )}
          {fileError && (
            <Text size="xs" c="red" mt="xs" ta="center">
              {fileError}
            </Text>
          )}
        </Paper>

        <Stack gap="md">
          <Paper withBorder p="md" radius="md">
            <Select
              label={t("documents.category")}
              placeholder={t("documents.selectCategory")}
              data={categoryOptions}
              searchable
              value={form.values.category_id ? String(form.values.category_id) : null}
              onChange={(val) => form.setFieldValue("category_id", val ? Number(val) : null)}
            />
            <TextInput
              label={t("documents.description")}
              placeholder={t("documents.descriptionPlaceholder")}
              mt="md"
              {...form.getInputProps("description")}
            />
            <DateInput
              label={t("documents.validUntil")}
              placeholder=""
              valueFormat="YYYY-MM-DD"
              minDate={new Date()}
              clearable
              mt="md"
              value={form.values.valid_until ? new Date(form.values.valid_until) : null}
              onChange={(val) => form.setFieldValue("valid_until", val ? val.toISOString().split("T")[0] : null)}
            />
            {!isEdit && (
              <Switch
                label={t("documents.public")}
                checked={form.values.public === 1}
                onChange={(e) => form.setFieldValue("public", e.currentTarget.checked ? 1 : 0)}
                mt="md"
              />
            )}
            {isEdit && doc && (
              <Text size="sm" mt="md">
                {doc.public ? (
                  <Badge variant="light" color="yellow">{t("documents.public")}</Badge>
                ) : (
                  <Badge variant="light" color="blue">{t("documents.private")}</Badge>
                )}
              </Text>
            )}
          </Paper>

          {roleOptions.length > 0 && (
            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={600} mb="xs">{t("documents.visibility")}</Text>
              <Text size="xs" c="dimmed" mb="sm">{t("documents.visibilityHelp")}</Text>
              <Checkbox.Group value={form.values.rules} onChange={(val) => form.setFieldValue("rules", val)}>
                <Stack>
                  {roleOptions.map((role: { value: string; label: string }) => (
                    <Checkbox key={role.value} value={role.value} label={role.label} />
                  ))}
                </Stack>
              </Checkbox.Group>
            </Paper>
          )}
        </Stack>
      </SimpleGrid>

    </form>
  )
})

export default DocumentFormContent
